
import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  TextField,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Autocomplete,
  Typography,
  Collapse,
  Divider,
  Chip,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { getAllEnquiries } from "../../../Services/Services";
import { deleteEnquiry, updateEnquiry } from "../../../Services/Services";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Print from "@mui/icons-material/Print";
import { Tooltip } from "@mui/material";
import { handlePrint } from "./EnquiryPrint";

const EnquiryList = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [enquiryToDelete, setEnquiryToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [enquiryToEdit, setEnquiryToEdit] = useState(null);
  const [customerSearch, setCustomerSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [expandedEnquiry, setExpandedEnquiry] = useState(null);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const fetchEnquiries = async () => {
      const data = await getAllEnquiries();
      setEnquiries(data);
    };
    fetchEnquiries();
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  const handleEditClick = (enquiry) => {
    setEnquiryToEdit({ ...enquiry });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!enquiryToEdit || !enquiryToEdit.enquiry_id) return;

    const response = await updateEnquiry(
      enquiryToEdit.enquiry_id,
      enquiryToEdit
    );

    if (response) {
      setEnquiries((prev) =>
        prev.map((enq) =>
          enq.enquiry_id === enquiryToEdit.enquiry_id ? enquiryToEdit : enq
        )
      );
      toast.success("Enquiry updated successfully!");
      setEditDialogOpen(false);
    } else {
      toast.error("Failed to update enquiry.");
    }
  };

  const statusOptions = [
    "New",
    "Under Review",
    "Quoted",
    "Converted",
    "Closed - Lost",
  ];

  const enquirySources = [
    "Website",
    "Phone Call",
    "Email",
    "Sales Visit",
    "Reference",
    "Other",
  ];

  const confirmDelete = async () => {
    if (!enquiryToDelete || !enquiryToDelete.enquiry_id) {
      toast.error("Invalid enquiry ID. Cannot delete.");
      return;
    }

    const response = await deleteEnquiry(enquiryToDelete.enquiry_id);

    if (response) {
      setEnquiries(
        enquiries.filter((e) => e.enquiry_id !== enquiryToDelete.enquiry_id)
      );
      toast.success("Enquiry deleted successfully!");
    } else {
      toast.error("Failed to delete enquiry.");
    }

    setDeleteDialogOpen(false);
    setEnquiryToDelete(null);
  };

  const filteredEnquiries = enquiries.filter(
    (enq) =>
      enq.customer_name.toLowerCase().includes(customerSearch.toLowerCase()) &&
      enq.company_name.toLowerCase().includes(companySearch.toLowerCase())
  );

  const handleDeleteClick = (enquiry) => {
    setEnquiryToDelete(enquiry);
    setDeleteDialogOpen(true);
  };

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "New":
        return "#2196f3";
      case "Under Review":
        return "#ff9800";
      case "Quoted":
        return "#9185a1ff";
      case "Converted":
        return "#4caf50";
      case "Closed - Lost":
        return "#f44336";
      default:
        return "#757575";
    }
  };

  return (
    <Box sx={{ px: 0, pt: 0, mt: { xs: 3, md: -3 }, maxWidth: 1200, mx: "auto" }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* 🔍 FILTERS */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label="Search by Customer"
              size="small"
              value={customerSearch}
              onChange={(e) => setCustomerSearch(e.target.value)}
              sx={{
                "& .MuiInputBase-root": {
                  height: 32,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              label="Search by Company"
              size="small"
              value={companySearch}
              onChange={(e) => setCompanySearch(e.target.value)}
              sx={{
                "& .MuiInputBase-root": {
                  height: 32,
                  borderRadius: 0,
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.7rem",
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              onClick={() => navigate("/dashboard/Createenquiry")}
              startIcon={<AddIcon />}
              sx={{
                height: 32,
                fontSize: "0.7rem",
                borderRadius: 0,
              }}
            >
              Enquiry
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* 📱 MOBILE VIEW (CARDS) */}
      {isMobile ? (
        <Box sx={{ bgcolor: "background.paper" }}>
          {filteredEnquiries.map((e, index) => (
            <Box key={e.enquiry_id}>
              <Box
                onClick={() =>
                  setExpandedEnquiry(
                    expandedEnquiry === e.enquiry_id ? null : e.enquiry_id
                  )
                }
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1.5,
                  cursor: "pointer",
                  bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                  "&:active": {
                    bgcolor: "#e3f2fd",
                  },
                  borderLeft: `4px solid ${getStatusColor(e.status)}`,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "#666",
                        minWidth: 25,
                      }}
                    >
                      #{index + 1}
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "0.85rem",
                        flex: 1,
                      }}
                    >
                      {e.customer_name}
                    </Typography>
                    <Chip
                      label={e.status}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.65rem",
                        bgcolor: getStatusColor(e.status),
                        color: "#fff",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                  </Box>

                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "#666",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <span style={{ fontWeight: 500 }}>
                      {e.company_name.substring(0, 20)}
                    </span>
                    <span style={{ color: "#999" }}>•</span>
                    <span style={{ fontSize: "0.7rem" }}>{e.phone}</span>
                  </Typography>
                </Box>

                <IconButton size="small">
                  {expandedEnquiry === e.enquiry_id ? (
                    <ExpandLess fontSize="small" />
                  ) : (
                    <ExpandMore fontSize="small" />
                  )}
                </IconButton>
              </Box>

              <Collapse
                in={expandedEnquiry === e.enquiry_id}
                timeout="auto"
                unmountOnExit
              >
                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                    borderLeft: `4px solid ${getStatusColor(e.status)}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 0.8,
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        EMAIL
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {e.email}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        CITY
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {e.city}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        ENQUIRY TYPE
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {e.enquiry_source}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                        }}
                      >
                        FOLLOW-UP
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.72rem",
                          fontWeight: 500,
                          color: "#555",
                        }}
                      >
                        {new Date(e.follow_up_date).toLocaleDateString("en-GB")}
                      </Typography>
                    </Box>
                  </Box>

                  {e.notes && e.notes.length > 0 && (
                    <Box sx={{ mb: 0.8 }}>
                      <Typography sx={{ fontSize: "0.7rem", color: "#999", letterSpacing: "0.5px", mb: 0.3 }}>
                        NOTES
                      </Typography>
                      <Typography sx={{ fontSize: "0.7rem", color: "#555", fontStyle: "italic", lineHeight: 1.3 }}>
                        {Array.isArray(e.notes)
                          ? e.notes.map(n => n.note_text).join(" | ")
                          : e.notes}
                      </Typography>
                    </Box>
                  )}

                  <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 0.6,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleEditClick(e);
                      }}
                      sx={{
                        bgcolor: "#e3f2fd",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: "#bbdefb",
                        },
                      }}
                    >
                      <EditIcon sx={{ fontSize: "1rem", color: "#1976d2" }} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteClick(e);
                      }}
                      sx={{
                        bgcolor: "#ffebee",
                        borderRadius: "6px",
                        width: 32,
                        height: 32,
                        "&:hover": {
                          bgcolor: "#ffcdd2",
                        },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: "1rem", color: "#d32f2f" }} />
                    </IconButton>
                  </Box>
                </Box>
              </Collapse>

              <Divider />
            </Box>
          ))}
        </Box>
      ) : (
        /* 💻 DESKTOP TABLE VIEW (ORIGINAL) */
        <TableContainer
          component={Paper}
          sx={{ maxHeight: "88vh", overflow: "auto" }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              minWidth: 1600,
              "& td, & th": {
                textAlign: "center",
                whiteSpace: "nowrap",
                borderBottom: "1px solid #ccc",
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "#f5f5f5",
                  "& th": {
                    fontWeight: "bold",
                    fontSize: "0.8rem",
                    color: "#333",
                    borderBottom: "1px solid #999",
                    backgroundColor: "#f5f5f5",
                  },
                }}
              >
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    backgroundColor: "#f5f5f5",
                    zIndex: 3,
                  }}
                >
                  S.No.
                </TableCell>
                <TableCell>Customer Name</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Enquiry Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Followup Date</TableCell>
                <TableCell>Status</TableCell>
                {/* <TableCell>Notes</TableCell> */}
                <TableCell
                  sx={{
                    position: "sticky",
                    right: 0,
                    backgroundColor: "#f5f5f5",
                    zIndex: 3,
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEnquiries.map((e, idx) => (
                <TableRow
                  hover
                  key={idx}
                  sx={{
                    paddingRight: "13px !important",
                    paddingLeft: "13px !important",
                    backgroundColor: idx % 2 === 0 ? "#fafafa" : "#ffffff",
                    "&:hover": {
                      backgroundColor: "#e3f2fd",
                      "& td": {
                        backgroundColor: "#e3f2fd !important",
                      },
                    },
                    "& td": {
                      fontSize: "0.75rem",
                      padding: 0,
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#fafafa",
                      zIndex: 2,
                    }}
                  >
                    {idx + 1}
                  </TableCell>
                  <TableCell>{e.customer_name}</TableCell>
                  <TableCell>{e.company_name}</TableCell>
                  <TableCell>
                    <Tooltip title={e.email} arrow>
                      <span>{truncateText(e.email, 20)}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{e.phone}</TableCell>
                  <TableCell>{e.city}</TableCell>
                  <TableCell>{e.country}</TableCell>
                  <TableCell>{e.enquiry_source}</TableCell>
                  <TableCell>
                    {new Date(e.created_at + "T00:00:00").toLocaleDateString("en-GB")}
                  </TableCell>
                  <TableCell>
                    {new Date(e.follow_up_date + "T00:00:00").toLocaleDateString("en-GB")}
                  </TableCell>
                  <TableCell>{e.status}</TableCell>
                  {/* <TableCell>{e.notes}</TableCell> */}
                  <TableCell
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#fff",
                      zIndex: 2,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate("/dashboard/Createenquiry", {
                          state: { data: e },
                        })}
                      color="primary"
                      sx={{ padding: 0, mx: 0.5 }}
                    >
                      <EditIcon transform="scale(0.6)" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(e)}
                      color="error"
                      sx={{ padding: 0, mx: 0.5 }}
                    >
                      <DeleteIcon transform="scale(0.6)" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handlePrint(e)}
                      color="info"
                      sx={{ padding: 0, mx: 0.5 }}
                    >
                      <Print transform="scale(0.6)" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ❌ DELETE DIALOG */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete enquiry from{" "}
          {enquiryToDelete?.customer_name}?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Close</Button>
          <Button color="error" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✏️ EDIT DIALOG */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Enquiry</DialogTitle>
        <DialogContent dividers>
          <Box component={Grid} container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Customer Name"
                fullWidth
                value={enquiryToEdit?.customer_name || ""}
                onChange={(e) =>
                  setEnquiryToEdit({
                    ...enquiryToEdit,
                    customer_name: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Company"
                fullWidth
                value={enquiryToEdit?.company_name || ""}
                onChange={(e) =>
                  setEnquiryToEdit({
                    ...enquiryToEdit,
                    company_name: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                fullWidth
                value={enquiryToEdit?.email || ""}
                onChange={(e) =>
                  setEnquiryToEdit({ ...enquiryToEdit, email: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone"
                fullWidth
                value={enquiryToEdit?.phone || ""}
                onChange={(e) =>
                  setEnquiryToEdit({ ...enquiryToEdit, phone: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                fullWidth
                value={enquiryToEdit?.city || ""}
                onChange={(e) =>
                  setEnquiryToEdit({ ...enquiryToEdit, city: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                fullWidth
                options={enquirySources}
                value={enquiryToEdit?.enquiry_source || ""}
                onChange={(e, newValue) =>
                  setEnquiryToEdit({
                    ...enquiryToEdit,
                    enquiry_source: newValue || "",
                  })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Enquiry Type" required />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                fullWidth
                value={enquiryToEdit?.enquiry_date || ""}
                onChange={(e) =>
                  setEnquiryToEdit({
                    ...enquiryToEdit,
                    enquiry_date: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={statusOptions}
                value={enquiryToEdit?.status || ""}
                onChange={(e, newValue) =>
                  setEnquiryToEdit({ ...enquiryToEdit, status: newValue || "" })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Status" fullWidth />
                )}
              />
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Close</Button>
          <Button
            onClick={handleEditSubmit}
            color="primary"
            variant="contained"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnquiryList;
